import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

export default class Menu {
  static init(...parameters) {
    return new Menu(...parameters);
  }

  constructor(container) {
    this.container = container;
    this.menuBar = this.container.querySelector('.menu-bar');
    this.menusContainer = this.container.querySelector('.menu-container');
    this.subMenusContainer = this.container.querySelector('.sub-menus');
    this.toggleMenuButton = this.container.querySelector('.hamburger-button');
    this.menuItemsWithChildren = document.querySelectorAll(
      '.primary-menu li.-has-children',
    );
    this.mobileMenuItemsWithChildren = document.querySelectorAll(
      '.-primary-menu-mobile li.-has-children',
    );

    this.mobileSearchOpen = false;
    this.clickOnSearchElement = false;
    this.menuWidth = document.body.offsetWidth;

    this.currentPageYOffset = null;

    let passiveSupported = false;
    try {
      const options = {
        // This function will be called when the browser attempts to access the passive property.
        get passive() {
          passiveSupported = true;
          return undefined;
        },
      };
      window.addEventListener('testPassive', null, options);
      window.removeEventListener('testPassive', null, options);
    } catch (ignored) {
      passiveSupported = false;
    }

    let disableNonActiveMenuDebouncing = false;
    const disableNonActiveMenuDebounce = () => {
      if (!disableNonActiveMenuDebouncing) {
        disableNonActiveMenuDebouncing = true;
        window.requestAnimationFrame(() => {
          this.disableNonActiveMenu();
          disableNonActiveMenuDebouncing = false;
        });
      }
    };
    window.addEventListener(
      'resize',
      disableNonActiveMenuDebounce,
      passiveSupported ? { passive: true } : false,
    );
    disableNonActiveMenuDebounce();

    let updatePageYOffsetDebouncing = false;
    const updatePageYOffsetDebounce = () => {
      if (!updatePageYOffsetDebouncing) {
        updatePageYOffsetDebouncing = true;
        window.requestAnimationFrame(() => {
          this.updatePageYOffset();
          updatePageYOffsetDebouncing = false;
        });
      }
    };
    window.addEventListener(
      'resize',
      updatePageYOffsetDebounce,
      passiveSupported ? { passive: true } : false,
    );
    window.addEventListener(
      'orientationchange',
      updatePageYOffsetDebounce,
      passiveSupported ? { passive: true } : false,
    );
    window.addEventListener(
      'scroll',
      updatePageYOffsetDebounce,
      passiveSupported ? { passive: true } : false,
    );
    updatePageYOffsetDebounce();

    /* Toggle menus */
    if (this.toggleMenuButton) {
      this.toggleMenuButton.addEventListener('click', () => {
        this.toggleFullscreenMenu();
      });
    }

    // Handle keyboard navigation within menu for accessibility (focus trap)
    this.menusContainer.addEventListener('keydown', e => this.trapFocus(e, this.menusContainer));

    // Handle Escape key press for menu navigation
    this.container.addEventListener('keydown', (e) => {
      // Check if Escape key was pressed (support both key and keyCode for browser compatibility)
      if (e.key === 'Escape' || e.keyCode === 27) {
        // Determine where the user is currently focused within the menu structure
        const isInChildMenu = e.target.closest('.child-menu');
        const isInFullscreenMenu = e.target.closest('.menu-container');
        const isOpenMenuItem = e.target.closest('.-open.-has-children');

        // If user is in a submenu (child menu), close it and focus on parent menu item
        if (isInChildMenu || isOpenMenuItem) {
          const parentItem = e.target.closest('.-has-children');
          const childMenu = parentItem.querySelector('.child-menu');
          this.toggleChildMenu(parentItem, childMenu);
          parentItem.querySelector('a, button').focus();
          // Return focus to parent menu item
        } else if (isInFullscreenMenu) {
          // If user is in the main fullscreen menu, close it and focus on hamburger button
          this.toggleMenuButton.focus(); // Return focus to menu toggle button
          this.toggleFullscreenMenu();
        }
      }
    });


    if (this.menuItemsWithChildren.length > 0) {
      [...this.menuItemsWithChildren].forEach((item) => {
        this.addChildClickEventListener(item);

        // Add focusout event listener to handle focus changes
        item.addEventListener('focusout', () => {
          // Check if the new focus target is outside this menu item
          setTimeout(() => {
            if (!item.contains(document.activeElement)) {
              const childMenu = item.querySelector('.child-menu');
              if (item.classList.contains('-open')) {
                this.toggleChildMenu(item, childMenu);
              }
            }
          }, 0);
        });
      });

      document.body.addEventListener('click', (event) => {
        const openChildMenu = this.container.querySelector('.child-menu.-open');
        const menuBarWrapper = this.container.querySelector('.menu-bar-wrapper');
        if (
          openChildMenu
          && event.target !== openChildMenu
          && !openChildMenu.contains(event.target)
          && event.target !== menuBarWrapper
          && !menuBarWrapper.contains(event.target)
        ) {
          this.closeChildMenus();
        }
      });
    }

    if (this.mobileMenuItemsWithChildren.length > 0) {
      [...this.mobileMenuItemsWithChildren].forEach((item) => {
        this.addChildClickEventListener(item);

        // Add focusout event listener to handle focus changes, close child menu when focus is lost
        item.addEventListener('focusout', () => {
          setTimeout(() => {
            if (!item.contains(document.activeElement)) {
              const childMenu = item.querySelector('.child-menu');
              if (item.classList.contains('-open')) {
                this.toggleChildMenu(item, childMenu);
              }
            }
          }, 0);
        });
      });
    }

    /* Search */
    // this.searchWrapper = this.container.querySelector('.search-wrapper');
    // this.mobileSearchView = this.searchWrapper.querySelector('.mobile-search-content');
    // this.searchForm = this.searchWrapper.querySelector('.search-form');
    // this.searchInput = this.searchWrapper.querySelector('.search-input');
    // this.mobileSearchForm = this.searchWrapper.querySelector('.mobile-search-form');
    // this.mobileSearchInput = this.searchWrapper.querySelector('.mobile-search-input');
    // this.searchButtonIcon = this.searchWrapper.querySelector('.search-button');
    // this.mobileSearchButton = this.searchWrapper.querySelector('.mobile-search-button');

    // this.searchValue = '';

    // this.placeholderDefault = this.searchInput.placeholder;

    // this.setupSearchEventListeners();

    // this.languageSelect = this.container.querySelector('.language-select form select');
    // this.setupLanguageSelectEventListeners();
  }

  updatePageYOffset() {
    const pageYOffset = window.pageYOffset
      || document.documentElement.scrollTop
      || document.body.scrollTop
      || 0;
    if (
      this.currentPageYOffset === null
      || this.currentPageYOffset !== pageYOffset
    ) {
      this.currentPageYOffset = pageYOffset;

      const { container } = this;
      if (container) {
        if (this.currentPageYOffset > 100) {
          container.classList.add('-collapsed');
        } else {
          container.classList.remove('-collapsed');
        }
      }
    }
  }

  /* Disable duplicated menu for accesibility reasons */
  disableNonActiveMenu() {
    const desktopMenu = document.querySelector('.primary-menu');
    const mobileMenu = document.querySelector('.-primary-menu-mobile');
    if (desktopMenu && getComputedStyle(desktopMenu).display === 'none') {
      desktopMenu.setAttribute('aria-hidden', 'true');
      mobileMenu.removeAttribute('aria-hidden');
      this.desktopMenuActive = false;
      this.mobileMenuActive = true;
    }
    if (mobileMenu && getComputedStyle(mobileMenu).display === 'none') {
      mobileMenu.setAttribute('aria-hidden', 'true');
      desktopMenu.removeAttribute('aria-hidden');
      this.desktopMenuActive = true;
      this.mobileMenuActive = false;
      if (this.mobileSearchOpen) {
        this.closeMobileSearch();
      }
    }
  }

  trapFocus(event, element) {
    const focusableElements = element.querySelectorAll(
      'a[href]:not([disabled]), button:not([disabled])',
    );
    const hamburgerButton = this.toggleMenuButton;
    const firstElement = hamburgerButton || focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const isTabPressed = event.key === 'Tab';
    if (!isTabPressed) {
      return;
    }

    if (event.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else if (document.activeElement === lastElement) {
      // Tab
      event.preventDefault();
      firstElement.focus();
    }
  }

  toggleFullscreenMenu() {
    const { classList } = this.container;
    if (classList.contains('-active')) {
      classList.remove('-active');
      enableBodyScroll(this.menusContainer);
      classList.add('-animate-out');
      this.menusContainer.setAttribute('inert', '');

      // Close all open child menus using toggleChildMenu
      const openMenuItems = this.container.querySelectorAll(
        '.-has-children.-open',
      );
      openMenuItems.forEach((item) => {
        const childMenu = item.querySelector('.child-menu');
        this.toggleChildMenu(item, childMenu);
      });

      setTimeout(() => {
        classList.remove('-animate-out');
        this.menusContainer.scrollTop = 0;
      }, 800);
      this.toggleMenuButton.setAttribute('aria-expanded', 'false');
    } else {
      if (this.desktopMenuActive) this.closeChildMenus();
      classList.add('-active');
      this.menusContainer.removeAttribute('inert');
      this.toggleMenuButton.setAttribute('aria-expanded', 'true');
      disableBodyScroll(this.menusContainer, {
        reserveScrollBarGap: true,
        paddingRightElement: this.menuBar,
      });
    }
  }

  toggleChildMenu(item, childmenu) {
    const trigger = item.querySelector('a, button');

    if (item.classList.contains('-open')) {
      // Close child menu
      item.classList.remove('-open');
      childmenu.classList.remove('-open');
      childmenu.setAttribute('aria-hidden', 'true');
      childmenu.setAttribute('inert', '');
      this.container.classList.remove('-open-child-menu');
      this.container.classList.add('-animate-out');

      if (trigger.tagName === 'BUTTON') {
        trigger.setAttribute('aria-expanded', 'false');
      }
      setTimeout(() => this.container.classList.remove('-animate-out'), 800);
    } else {
      // Open child menu
      item.classList.add('-open');
      childmenu.classList.add('-open');
      childmenu.setAttribute('aria-hidden', 'false');
      childmenu.removeAttribute('inert');
      this.container.classList.add('-open-child-menu');
      if (trigger.tagName === 'BUTTON') {
        trigger.setAttribute('aria-expanded', 'true');
      }

      if (
        this.desktopMenuActive
        && this.container.classList.contains('-active')
      ) {
        this.container.classList.remove('-active');
        enableBodyScroll(this.menusContainer);
      }
    }
  }

  addChildClickEventListener(item) {
    const trigger = item.querySelector('a, button');
    const childmenu = item.querySelector('.child-menu');

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      this.toggleChildMenu(item, childmenu);
    });
  }

  closeChildMenus() {
    if (this.menuItemsWithChildren.length > 0) {
      [...this.menuItemsWithChildren].forEach((item) => {
        const childmenu = item.querySelector('.child-menu');
        childmenu.classList.add('-animate-out-fast');
        childmenu.setAttribute('aria-hidden', 'true');
        childmenu.setAttribute('inert', '');
        setTimeout(() => childmenu.classList.remove('-animate-out-fast'), 200);
        item.classList.remove('-open');
        childmenu.classList.remove('-open');
        this.container.classList.remove('-open-child-menu');
      });
    }
  }

  setupLanguageSelectEventListeners() {
    if (this.languageSelect) {
      this.languageSelect.addEventListener('change', () => {
        const { value } = this.languageSelect.options[this.languageSelect.selectedIndex];
        if (value) {
          window.location.href = value;
        }
      });
    }
  }

  setupSearchEventListeners() {
    this.searchInput.addEventListener('focus', (event) => {
      this.searchWrapper.classList.add('-highlight');
      this.searchInput.placeholder = '';
      this.onSearchInputFocus(event.target, this.searchInput);
    });

    this.searchWrapper.addEventListener('click', () => {
      if (this.desktopMenuActive) {
        this.searchWrapper.classList.add('-highlight');
        this.searchInput.placeholder = '';
        if (this.searchInput) this.searchInput.focus();
      }
    });

    this.searchInput.addEventListener('focusout', () => {
      if (this.searchInput.value === '') {
        this.searchInput.placeholder = this.placeholderDefault;
      }
      this.searchWrapper.classList.remove('-highlight');
    });

    this.searchInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        this.submitForm(this.searchForm);
        return false;
      }
      return true;
    });

    this.searchButtonIcon.addEventListener('click', () => {
      if (this.desktopMenuActive) {
        this.searchValue = this.searchInput.value;
        if (
          this.searchValue === ''
          || !this.searchWrapper.classList.contains('-focus')
        ) return;
        this.submitForm(this.searchForm);
      } else if (!this.mobileSearchOpen) {
        this.openMobileSearch();
      } else {
        this.closeMobileSearch();
      }
    });

    this.mobileSearchButton.addEventListener('click', () => {
      this.searchValue = this.mobileSearchInput.value;
      if (this.searchValue === '') return;
      this.submitForm(this.mobileSearchForm);
    });

    this.mobileSearchInput.addEventListener('focus', (event) => {
      this.mobileSearchView.classList.add('-focus');
      this.onSearchInputFocus(event.target, this.mobileSearchInput);
    });

    this.mobileSearchInput.addEventListener('focusout', () => {
      if (this.mobileSearchInput.value !== '') {
        return;
      }
      if (this.searchInput.value === '') {
        this.searchInput.placeholder = this.placeholderDefault;
      }
      this.searchWrapper.classList.remove('-highlight');
    });

    this.mobileSearchInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        this.submitForm(this.mobileSearchForm);
        return false;
      }
      return true;
    });

    window.addEventListener('click', (event) => {
      if (event.target.classList.contains('-search-element')) {
        this.clickOnSearchElement = true;
      } else {
        this.clickOnSearchElement = false;
        if (this.searchInput.value === '') {
          this.searchInput.placeholder = this.placeholderDefault;
        }
        this.searchWrapper.classList.remove('-highlight');
      }
    });
  }

  openMobileSearch() {
    this.container.classList.add('-active-mobile-search');
    this.mobileSearchOpen = true;
    // setTimeout(() => this.mobileSearchInput.focus(), 800);
    disableBodyScroll(this.mobileSearchView, {
      reserveScrollBarGap: true,
      paddingRightElement: this.menuBar,
    });
  }

  closeMobileSearch() {
    this.container.classList.remove('-active-mobile-search');
    this.container.classList.add('-animate-out');
    this.container.classList.add('-animate-out-menu-bar');
    setTimeout(() => {
      this.container.classList.remove('-animate-out');
      this.container.classList.remove('-animate-out-menu-bar');
    }, 800);
    this.mobileSearchOpen = false;
    enableBodyScroll(this.mobileSearchView);
  }

  // eslint-disable-next-line class-methods-use-this
  onSearchInputFocus(target, input) {
    target.setSelectionRange(0, input.value.length);
  }

  // eslint-disable-next-line class-methods-use-this
  submitForm(target) {
    target.submit();
  }
}

window.brix
  && window.brix.registerBlock
  && window.brix.registerBlock('brix-multi-menu', Menu.init);
